import CheckCircleIcon from 'components/ReusableComponents/Icons/CheckCircle'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import ExclamationIcon from 'components/ReusableComponents/Icons/Exclamation'
import moment from 'moment'
import { useSelector } from 'react-redux'
import ReactTooltip from 'react-tooltip'

import { features } from './constants'
import styles from './FeatureStatuses.module.scss'
import { generateSingleFeatureStatusWithReason } from './generateFeatureStatusWithReason'

function generateFeatureStatuses(hotelParameters) {
  return features.map(featureGroup => {
    const featuresWithStatus = featureGroup.features.map(feature => {
      const featureStatus = generateSingleFeatureStatusWithReason({
        title: feature.title,
        hotelParameters
      })
      return {
        title: feature.title,
        status: featureStatus.status,
        reason: featureStatus?.reason || ''
      }
    })

    return {
      featureGroupTitle: featureGroup.featureGroupTitle,
      features: featuresWithStatus
    }
  })
}

const FeatureRow = ({ title, status, reason }) => (
  <div className={styles.featureRow} data-tip={reason} data-for="featureStatusTooltip">
    <div className={styles.featureTitle}>{title}</div>
    <div className={styles.featureStatus}>
      {status === 'Good' && <CheckCircleIcon className={styles.checkIcon} />}
      {status === 'Bad' && <CrossIcon className={styles.crossIcon} />}
      {status === 'Warn' && <ExclamationIcon className={styles.exclamationIcon} />}
    </div>
  </div>
)

const FeatureStatuses = ({
  phoneNumbers,
  status,
  tcrBrandId,
  tcrCampaignId,
  inviteBackIsDisabled,
  reservationHotLine,
  serviceStartDate,
  onlySendsOTAMessages,
  reviewTripAdvisorPage,
  isChatBotEnabled,
  isUsingDefaultOpera,
  enrichedGuestPercentage,
  areMarketingCampaignsEnabled,
  areOTAMarketingCampaignsEnabled,
  areReviewCampaignsEnabled,
  OTArateCodesCount,
  hasChatbotResponses,
  isPmsSyncing
}) => {
  // existing data

  const hasPhoneNumber = phoneNumbers.filter(p => p.status === 'Active').length > 0

  const hasPhoneNumberWithVoiceAndText =
    phoneNumbers.filter(p => p.status === 'Active' && p.capability === 'VoiceAndText')
      .length > 0

  const isChurned = status === 'Churned'

  const isCampaignRegisteryComplete = tcrBrandId && tcrCampaignId

  const hasReservationHotline = !!reservationHotLine

  const hasServiceStarted = moment().isAfter(moment(serviceStartDate))

  const hasTripAdvisorLink = !!reviewTripAdvisorPage

  const activeServices = useSelector(s => s.hotelDetails.activeServices)
  const hasBasicService = activeServices.some(s => s.plan === 'Basic')
  const hasUnlimitedService = activeServices.some(s => s.plan === 'Unlimited')
  const hasCoreService = activeServices.some(s => s.plan === 'DirectfulCore')
  const hasMarketing = hasCoreService

  const hotelParameters = {
    isPmsSyncing,
    hasPhoneNumber,
    hasPhoneNumberWithVoiceAndText,
    isChurned,
    isCampaignRegisteryComplete,
    enrichedGuestPercentage,
    inviteBackIsDisabled,
    hasReservationHotline,
    hasServiceStarted,
    onlySendsOTAMessages,
    areMarketingCampaignsEnabled,
    areOTAMarketingCampaignsEnabled,
    OTArateCodesCount,
    areReviewCampaignsEnabled,
    hasTripAdvisorLink,
    hasMarketing,
    hasBasicService,
    hasUnlimitedService,
    hasCoreService,
    isChatBotEnabled,
    isUsingDefaultOpera,
    hasChatbotResponses
  }

  const featuresWithStatuses = generateFeatureStatuses(hotelParameters)

  const totalFeatureCount = featuresWithStatuses.reduce(
    (acc, featureGroup) => acc + featureGroup.features.length,
    0
  )

  const totalGoodFeatureCount = featuresWithStatuses.reduce(
    (acc, featureGroup) =>
      acc + featureGroup.features.filter(feature => feature.status !== 'Bad').length,
    0
  )

  return (
    <div className={styles.featureStatuses}>
      <ReactTooltip id="featureStatusTooltip" place="right" type="dark" effect="solid" />
      <div className={styles.titleHolder}>
        <span className={styles.boxTitle}>Feature status</span>
        <span
          className={styles.statusCountPill}
          style={{
            backgroundColor:
              totalFeatureCount === totalGoodFeatureCount ? '#D8FEDA' : '#F5E6E6',
            color: totalFeatureCount === totalGoodFeatureCount ? '#409144' : '#FF4545'
          }}
        >
          {totalGoodFeatureCount}/{totalFeatureCount} healthy
        </span>
      </div>
      <div className={styles.featuresHolder}>
        {generateFeatureStatuses(hotelParameters).map(
          ({ featureGroupTitle, features }) => (
            <div key={featureGroupTitle} className={styles.featureGroup}>
              {featureGroupTitle && (
                <div className={styles.featureGroupTitle}>{featureGroupTitle}</div>
              )}
              {features.map(({ title, status, reason }) => (
                <FeatureRow key={title} title={title} status={status} reason={reason} />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default FeatureStatuses
